import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { PRODUCT_TYPE_IDS, BRAND_PATHS, FEATURE_FLAGS } from "@chef/constants";
import {
  openModal,
  selectDeviationProducts,
  showNotification,
  useBillingQuery,
  usePickAndMixQuery,
  useRecommendedRecipesQuery,
} from "@chef/state-management";
import {
  useCalendarData,
  useExtendedBasketData,
  useFavorites,
  useFilters,
  useTrack,
  useLoyalty,
} from "@chef/state-management/hooks";
import { getCutoffDateForWeek } from "@chef/helpers";
import { FILTER_CATEGORIES } from "@chef/constants";
import {
  filterPickAndMix,
  generateFilterCategoriesFromRecipes,
  getBasketDataFromCalendar,
  getBasketFinances,
  getIsBeforeStartDateFromCalendar,
} from "@chef/state-management/helpers";
import { useEffectOnce } from "@chef/hooks";
import { FilterNavigation } from "@chef/smart-components";
import { useFlag } from "@chef/feature-flags";
import { isEqualStrings } from "@chef/utils/equal";

import type { WeekYear } from "@chef/types/common";

import {
  useCreateDeviationBasket,
  usePickAndMixProductWithPrice,
  useSortedPickAndMix,
} from "../../hooks";

import { PickAndMixCardGrid } from "../PickAndMixCardGrid";

import { intl } from "./MyMenuOptions.Intl";

export const MyMenuOptions = ({ week, year }: WeekYear) => {
  const router = useRouter();
  const { updateUrl } = useFilters();

  const dispatch = useDispatch();
  const track = useTrack();

  const selectedProducts = useSelector(selectDeviationProducts({ week, year }));

  const {
    data: billingData,
    isLoading: isLoadingBillingData,
    isError: isErrorBillingData,
  } = useBillingQuery();

  const {
    data: extendedBasketData,
    isLoading: isLoadingExtendedBasketData,
    isError: isErrorExtendedBasketData,
  } = useExtendedBasketData({ week, year });

  const portions = extendedBasketData.portions;

  const { isLoading: isLoadingCreateDeviationBasket } =
    useCreateDeviationBasket({ week, year });

  const { data: loyaltyData } = useLoyalty();

  const [currentFilter, setCurrentFilter] = useState<string>(
    FILTER_CATEGORIES.ALL_CATEGORIES_SLUG,
  );

  const { favorites, isReady: favoritesIsReady } = useFavorites();

  const {
    enabled: isPrioritiseRecommendationsEnabled,
    isReady: isPrioritiseRecommendationsReady,
  } = useFlag(FEATURE_FLAGS.PRIORITISE_RECOMMENDATIONS_FILTER);

  const { data: _recommendedRecipes } = useRecommendedRecipesQuery({
    week,
    year,
  });

  const {
    data: { pickAndMix } = {},
    isError: isErrorPickAndMix,
    isLoading: isLoadingPickAndMix,
  } = usePickAndMixQuery({
    productTypeId: PRODUCT_TYPE_IDS.PICKANDMIX,
    week,
    year,
  });

  const {
    calendar,
    isError: isErrorCalendar,
    isLoading: isLoadingCalendar,
  } = useCalendarData({
    week,
    year,
  });

  const filteredPickAndMix = useMemo(() => {
    return filterPickAndMix({
      pickAndMix: pickAndMix || [],
      cutoffDate: getCutoffDateForWeek({ week, year }),
      portions,
    });
  }, [pickAndMix, portions, week, year]);

  const { pickAndMix: pnmxWithPrices } = usePickAndMixProductWithPrice({
    week,
    year,
    sortedPickAndMix: filteredPickAndMix,
  });

  const { sortedPickAndMix } = useSortedPickAndMix({
    pickAndMix: pnmxWithPrices,
    week,
    year,
  });

  const recommendedProductIdsForWeek = useMemo(() => {
    const productIds = new Map<string, number>();
    const recommendedRecipes =
      _recommendedRecipes?.recommendedRecipes?.recipes ?? [];

    const sortedRecommendedRecipes = [...recommendedRecipes].sort(
      (a, b) => b.score - a.score,
    );

    for (const recipe of sortedRecommendedRecipes) {
      const productId = pnmxWithPrices
        .flat()
        .find((pnm) =>
          pnm.recipes.some((r) => r.mainRecipeId === recipe.mainRecipeId),
        )?.productId;

      if (productId) {
        productIds.set(productId, recipe.score);
      }
    }

    return productIds;
  }, [pnmxWithPrices, _recommendedRecipes]);

  const filters = useMemo(() => {
    if (!favoritesIsReady || !isPrioritiseRecommendationsReady) {
      return [];
    }

    const recipes = pnmxWithPrices.flatMap((pnm) =>
      pnm.recipes.map((r) => ({ ...r, productId: pnm.productId })),
    );

    return generateFilterCategoriesFromRecipes({
      recipes,
      currentFilter,
      recommendations: recommendedProductIdsForWeek,
      favorites,
      prioritiseRecommendations: !!isPrioritiseRecommendationsEnabled,
    });
  }, [
    pnmxWithPrices,
    recommendedProductIdsForWeek,
    currentFilter,
    favorites,
    favoritesIsReady,
    isPrioritiseRecommendationsEnabled,
    isPrioritiseRecommendationsReady,
  ]);

  const onCartClick = () => {
    dispatch(openModal({ id: "DEVIATION_CART", week, year }));
  };

  const isError =
    isErrorCalendar ||
    isErrorPickAndMix ||
    isErrorExtendedBasketData ||
    isErrorBillingData;

  const isLoading =
    router.isFallback ||
    isLoadingPickAndMix ||
    isLoadingCalendar ||
    isLoadingCreateDeviationBasket ||
    isLoadingExtendedBasketData ||
    isLoadingBillingData ||
    !favoritesIsReady;

  // Tracking
  useEffectOnce(() => {
    track("deviationUpdateStepViewed", {
      affiliation: "Frontend process",
      delivery_update_step: 1,
      delivery_update_step_title: intl.CHOOSE_DISHES,
      delivery_update_step_name: "userPickAndMix",
    });

    if (!selectedProducts) {
      return;
    }

    track("deviationUpdateStarted", {
      affiliation: "Frontend process",
      products: selectedProducts.map((bp) => ({
        product_id: bp.productId,
        variant_id: bp.variationId,
        quantity: bp.quantity,
        price: bp.price,
        category_id: bp.productTypeId,
      })),
      week,
      year,
      status: billingData?.billing.status,
      total: price,
    });
  }, !isLoading && !isError);

  useEffect(() => {
    const filter = router.query["filter"];
    if (filter) {
      if (typeof filter === "string") {
        setCurrentFilter(filter);
      } else {
        console.error("filter is not a string");
      }
    }
  }, [router.query]);

  const { mealboxBasket } =
    getBasketDataFromCalendar({
      calendar: calendar || [],
      week,
      year,
    }) || {};
  const shippingDetails = mealboxBasket?.shippingDetails;

  const plannedDeliveryInformation =
    shippingDetails?.plannedDeliveryInformation;

  const isBeforeStartWeek = getIsBeforeStartDateFromCalendar({
    calendar: calendar || [],
    week,
    year,
  });

  useEffectOnce(() => {
    router.push(BRAND_PATHS.MY_MENU_WEEK_HREF({ week, year }));
  }, !!calendar && !plannedDeliveryInformation?.actualTimeblockId);

  useEffectOnce(() => {
    showNotification({
      type: "error",
      message: intl.NO_DELIVERIES_BEFORE_START_WEEK,
    });
    router.push(BRAND_PATHS.MY_MENU_PAGE);
  }, !!calendar && !!isBeforeStartWeek);

  const filterInformation = filters.find((f) =>
    isEqualStrings(f.value, currentFilter),
  );

  const price = getBasketFinances({
    basketProducts: selectedProducts || [],
    customerFee: plannedDeliveryInformation?.customerFee,
    loyaltyLevel: loyaltyData?.currentLevel,
  }).total;

  return (
    <section
      className="w-full scroll-mt-24 md:scroll-mt-36"
      id="menu-options-section"
    >
      <h1 className="mt-8 mb-4">
        <strong>{intl.CHOOSE_DISHES}</strong>
      </h1>
      <FilterNavigation
        items={filters}
        value={currentFilter}
        onChange={(val) => {
          updateUrl(val);
        }}
        resetFilters={() => updateUrl(FILTER_CATEGORIES.ALL_CATEGORIES_SLUG)}
        className="mt-4"
        leftClassName="bg-gradient-to-r from-background via-background"
        rightClassName="bg-gradient-to-l from-background via-background"
        showGradients
      />

      <PickAndMixCardGrid
        items={sortedPickAndMix}
        filter={filterInformation}
        recommendations={recommendedProductIdsForWeek}
        portions={portions}
        resetFilters={() => updateUrl(FILTER_CATEGORIES.ALL_CATEGORIES_SLUG)}
        onModalAction={onCartClick}
        week={week}
        year={year}
        className="mt-2"
      />
    </section>
  );
};
